import React, { Fragment, useEffect, useMemo, useState } from "react";
import {inject, observer} from "mobx-react";
import {Dropdown} from "react-bootstrap";
import { flushCache, reload } from "../../queries/info";
import {toast} from "react-toastify";

import {Threshold} from "./Threshold";
import Checkbox, {RadioButtonGroup} from "../controls/Checkbox";
import {
  DEFAULT_CLIENT_SIM_THRESHOLD,
  DEFAULT_COMPOUND_SIMTYPE,
  DEFAULT_DSCONFIG,
  DEFAULT_MIN_EXPANSION,
  MIN_OTHER_THRESHOLD,
  MIN_SIM_THRESHOLD
} from "../../stores/SearchSessionStore";
import { captureMessage } from "../../lib/utils";
import Analytics from "../../lib/Analytics";

// Throws during render to test React error boundary capture
class ErrorBoundaryTrigger extends React.Component {
  render() {
    if (this.props.trigger) throw new Error("[TEST] Test Sentry error boundary");
    return null;
  }
}

export const Group = ({className = null, title = null, children}) => (
  <>
    {title && (<Dropdown.Header>{title}</Dropdown.Header>)}
    <div className={`dropdown-group${className ? ' ' + className : ''}`}>
      {children}
    </div>
  </>
);

/**
 * NavBarIconLabel
 * Stacks a glyphicon above a small ALL-CAPS text label.
 */
const NavBarIconLabel = ({ icon, label }) => (
  <span className="navbar-icon-label">
    <span className={'glyphicon ' + icon} aria-hidden="true" />
    <span className="navbar-icon-label__text">{label}</span>
  </span>
);

const SIM_DISABLE = "sim-disable"
const SIM_SUBSTRUCTURE = "sim-substructure"
const SIM_ENABLE = "sim-enable"

const SettingsMenu = ({
  className,
  appStatusStore,
  userStore,
  searchParams,
  modifySearchParameters,
  substructureRequired,
}) => {
  const {
    isAdmin = false,
    showAdmin = false,
    authData,
  } = userStore;
  const {
    ready: serverReady = false,
    versionInfo: {
      imaps_available: availableDsConfigs = [{name: "Loading...", datasets: [], index: 0}],
      sim_methods_available: compoundSimMethodsAvailable = [],
      sim_types_available: defaultSimThresholds = {},
    } = {},
  } = appStatusStore;

  // If any of the inputs is a substructure query, disallow turning off substructure param
  const {
    dsConfig = DEFAULT_DSCONFIG,
    queryEntities = [],
    simType,
    simMethod,
    simThreshold,
    simSubstructure,
    findRelated,
    indirect,
    minExpansion = null,
    densityExpansion = null,
  } = searchParams;

  const isCompoundSearch = queryEntities.some(el => el.category === "compound");
  // Quick reference for current sim state
  const simSetting = simSubstructure
                     ? SIM_SUBSTRUCTURE
                     : simType === "" ? SIM_DISABLE : SIM_ENABLE;

  // Keep track of these settings locally, since they get wiped from
  // searchParams if disabled
  const [lastSimType, setLastSimType] = useState(simType || null);
  const [lastSimMethod, setLastSimMethod] = useState(simMethod || null);
  const [lastMinExpansion, setLastMinExpansion] = useState(minExpansion || null);
  // Remember thresholds locally for subsequent recall when switching
  // or turning similarity on/off; avoid modifying the original reference
  const lastSimThresholds = useMemo(() => (
    {
      ...defaultSimThresholds,
      // Override these with better defaults
      sim: DEFAULT_CLIENT_SIM_THRESHOLD,
      chem: DEFAULT_CLIENT_SIM_THRESHOLD,
    }), [defaultSimThresholds]);

  const [showDropdown, setShowDropdown] = useState("");
  const [triggerErrorBoundary, setTriggerErrorBoundary] = useState(false);
  const [simulateStreamBlocked, setSimulateStreamBlocked] = useState(
    () => sessionStorage.getItem("simulate_sse_blocked") === "1"
  );
  const updateSimThreshold = (value) => {
    if (simType && value != null) {
      lastSimThresholds[simType] = value;
    }
    modifySearchParameters({simThreshold: value, minExpansion: null});
  }

  // Remember the last selected sim type, since disabling sets sim type to ""
  useEffect(() => {
    if (simType) {
      setLastSimType(simType);
    }
  }, [simType]);
  useEffect(() => {
    if (simThreshold) {
      lastSimThresholds[simType] = simThreshold;
    }
  }, [simThreshold, simType]);
  useEffect(() => {
    if (simMethod) {
      setLastSimMethod(simMethod);
    }
  }, [simMethod]);
  useEffect(() => {
    if (minExpansion) {
      setLastMinExpansion(minExpansion);
    }
  }, [minExpansion]);

  useEffect(() => {
    // Turn off find related by default if doing a compound search
    // Do not, however, disable the control.
    if (isCompoundSearch) {
      modifySearchParameters({findRelated: false});
    }
  }, [isCompoundSearch]);

  const showDSConfigs = availableDsConfigs.length > 1 || isAdmin;
  const cn = `${className ? ' ' + className : ''}${serverReady ? '' : ' icon-pulse'}`;
  const {compoundFPTypes} = appStatusStore;
  // Filter out pubchem unless admin
  const fpTypes = new Set(compoundFPTypes.filter(type => isAdmin || type !== "pubchem"));

  return (
    <span title="Settings" className="settings-menu">
      <Dropdown
        title="Settings"
        className={`settings-menu${cn}`}
        key="settings"
        autoClose="outside"
        disabled={!serverReady}
        focusFirstItemOnShow={true}
        onToggle={(isOpen) => setShowDropdown(isOpen)}
        show={showDropdown}
      >
        <Dropdown.Toggle disabled={!serverReady}><NavBarIconLabel icon="glyphicon-cog" label="Params" /></Dropdown.Toggle>
        <Dropdown.Menu rootCloseEvent="click" onClick={(e) => e.stopPropagation()}>
          {showDSConfigs && (
            <>
              <Dropdown.Item>
                <label>
                  Dataset Configuration
                  <select
                    disabled={availableDsConfigs.length === 1}
                    title={`${availableDsConfigs.reduce((result, item) => {
                      if (item.index === dsConfig) {
                        const ds = item.datasets;
                        return Array.isArray(ds) ? ds.join(", ") : (ds || "");
                      }
                      return result;
                    }, "")}`}
                    value={dsConfig}
                    onChange={e=> { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "dsConfig", value: e.target.value }); modifySearchParameters({dsConfig: e.target.value}); }}
                  >
                    {availableDsConfigs.map((item) => (
                      <option
                        key={item.index}
                        value={item.index}
                        title={Array.isArray(item.datasets) ? item.datasets.join(", ") : (item.datasets || "")}
                        >
                        {`${item.name} (${item.version})`}
                      </option>
                    ))}
                  </select>
                </label>
              </Dropdown.Item>
              <Dropdown.Divider />
            </>
          )}
          <Dropdown.Item
            onClick={() => modifySearchParameters({findRelated: !findRelated})}
            title={"Include the starting category in the search results to find commonality"}
          >
            <Checkbox
              checked={findRelated}
              label={"Find Related"}
              title={"Include the starting category in the search results to find commonality"}
              onChange={checked => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "findRelated", value: checked }); modifySearchParameters({findRelated: checked}); }}
            />
          </Dropdown.Item>
          {isAdmin && (
            <Dropdown.Item
              className={"admin"}
              onClick={() => modifySearchParameters({indirect: !indirect})}
              title={"Restrict search results to those without a direct connection to the query"}
            >
              <Checkbox
                checked={indirect}
                label={"Indirect"}
                title={"Restrict search results to those without a direct connection to the query"}
                onChange={checked => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "indirect", value: checked }); modifySearchParameters({indirect: checked}); }}
                />
            </Dropdown.Item>)}
          {densityExpansion !== null && (
            <Dropdown.Item
              onClick={() => modifySearchParameters({densityExpansion: !densityExpansion})}
              title={"Use density-based expansion for geneset search results"}
            >
              <Checkbox
                checked={!!densityExpansion}
                label={"Density Expansion"}
                title={"Use density-based expansion for geneset search results"}
                onChange={checked => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "densityExpansion", value: checked }); modifySearchParameters({densityExpansion: checked}); }}
              />
            </Dropdown.Item>
          )}
          <Dropdown.Divider/>
          <RadioButtonGroup disabled={substructureRequired} legend={""}>
            <Dropdown.Item
              onClick={() => modifySearchParameters({simType: "", simSubstructure: null})}
              disabled={substructureRequired}
              title="Disable similarity"
            >
              <Checkbox
                checkType={"radio"}
                checked={simSetting === SIM_DISABLE}
                label={"Disable Similarity"}
                title="Disable similarity"
                disabled={substructureRequired}
                onChange={() => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "simMode", value: "disable" }); modifySearchParameters({simType: "", simSubstructure: null}); }}
                />
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => modifySearchParameters({simSubstructure: true, simType})}
              disabled={substructureRequired}
              title="Include compounds for which the query is a substructure"
            >
              <Checkbox
                checkType={"radio"}
                checked={simSetting === SIM_SUBSTRUCTURE}
                disabled={substructureRequired}
                label={"Match Compound Substructure"}
                title="Include compounds for which the query is a substructure"
                onChange={() => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "simMode", value: "substructure" }); modifySearchParameters({simSubstructure: true, simType}); }}
                />
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => modifySearchParameters({simSubstructure: null,
                                                      simType: lastSimType || DEFAULT_COMPOUND_SIMTYPE,
                                                      simThreshold: lastSimThresholds[lastSimType || DEFAULT_COMPOUND_SIMTYPE]})}
              disabled={substructureRequired}
              title="Include compounds similar to the query"
            >
              <Checkbox
                checkType={"radio"}
                checked={simSetting === SIM_ENABLE}
                disabled={substructureRequired}
                label={"Use Compound Similarity"}
                title="Include compounds similar to the query"
                onChange={() => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "simMode", value: "similarity" }); modifySearchParameters({simType: lastSimType || DEFAULT_COMPOUND_SIMTYPE, simThreshold: lastSimThresholds[lastSimType || DEFAULT_COMPOUND_SIMTYPE], simSubstructure: null}); }}
              />
            </Dropdown.Item>
          </RadioButtonGroup>
          <Group className={simSetting === SIM_DISABLE ? "disabled" : ""}>
            <RadioButtonGroup
              legendClassName="dropdown-header"
              legend={"Similarity Extent"}
              title={"Determine when to stop checking compounds of lower similarity"}
            >
              <Threshold
                label={"Target Threshold"}
                title={"Automatically include decreasingly similar compounds until at least this many targets have been found (down to usimilarity threshold)"}
                checkType={"radio"}
                isSelected={minExpansion != null}
                value={lastMinExpansion || DEFAULT_MIN_EXPANSION}
                onToggle={checked => modifySearchParameters({minExpansion: checked ? lastMinExpansion || DEFAULT_MIN_EXPANSION : null,
                                                              simThreshold: null})}
                onValueChange={value => modifySearchParameters({minExpansion: value, simThreshold: null})}
                disabled={simSetting !== SIM_ENABLE}
                thresholdDisabled={minExpansion === null}
                min={1} max={10000} step={1} precision={null}
              />
              <Threshold
                label={"Similarity Threshold"}
                title={"Minimum similarity score to use when searching"}
                checkType={"radio"}
                isSelected={minExpansion === null}
                value={simThreshold ?? lastSimThresholds[simType || lastSimType || DEFAULT_COMPOUND_SIMTYPE]}
                onToggle={checked => updateSimThreshold(checked ? (simThreshold ?? lastSimThresholds[simType || lastSimType || DEFAULT_COMPOUND_SIMTYPE]) : null)}
                onValueChange={value => updateSimThreshold(value)}
                disabled={simSetting !== SIM_ENABLE}
                thresholdDisabled={simSetting === SIM_SUBSTRUCTURE || minExpansion != null}
                min={simSetting === SIM_DISABLE || new Set(["sim", "chem", "pubchem"]).has(simType) ? MIN_SIM_THRESHOLD : MIN_OTHER_THRESHOLD}
                max={1} step={0.05} precision={2}
              />
            </RadioButtonGroup>
            <Dropdown.Item
              title={"Use the selected fingerprint type"}
              disabled={simSetting === SIM_DISABLE}
            >
              <label>Fingerprint Type
                {(simType === "pubchem" || lastSimType === "pubchem") && (
                  <span
                    className="glyphicon glyphicon-warning-sign"
                    style={{color: "#f0ad4e", marginLeft: "0.5rem", fontSize: "0.9rem"}}
                    title="Do not use this option for proprietary data"
                  />
                )}
                <select
                  value={simType || lastSimType || DEFAULT_COMPOUND_SIMTYPE}
                  onChange={e => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "simType", value: e.target.value }); modifySearchParameters({simType: e.target.value, simThreshold: lastSimThresholds[e.target.value]}); }}
                  disabled={simSetting === SIM_DISABLE}
                >
                  {Array.from(fpTypes).sort().map((key) => (
                    <option
                      key={key}
                      value={key}
                    >
                      {key}
                    </option>
                  ))}
                </select>
              </label>
            </Dropdown.Item>
            <Dropdown.Item
              title={"Use the selected measure for fingerprint similarity"}
              disabled={simSetting !== SIM_ENABLE || simType === "pubchem"}
            >
              <label>Measure
                <select
                  value={simMethod || lastSimMethod || ""}
                  disabled={simSetting !== SIM_ENABLE || simType === "pubchem"}
                  onChange={e => { Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "simMethod", value: e.target.value }); modifySearchParameters({simMethod: e.target.value}); }}
                >
                  {compoundSimMethodsAvailable.map((key) => (
                    <option
                      key={key}
                      value={key}
                    >
                      {key}
                    </option>
                  ))}
                </select>
              </label>
            </Dropdown.Item>
            <Threshold
              className={"admin"}
              label={"Gene/Compound List"}
              title={"Threshold for measuring similarity between two lists of the same type (e.g. RNA Profiles or Pathways)"}
              value={simThreshold}
              onValueChange={value => modifySearchParameters({simThreshold: value})}
              onEnter={() => setShowDropdown(false)}
              disabled={simSetting !== SIM_ENABLE}
              min={0} max={1} step={0.05} precision={2}
            />
          </Group>
          {isAdmin && (
            <>
              <Dropdown.Divider className="admin" />
              <Dropdown.Item className={"admin-toggle"} >
                <Checkbox
                  label={"Show Admin Settings"}
                  title={`Select to make more admin settings visible`}
                  checked={showAdmin}
                  onChange={s => userStore.showAdmin = s}
                />
              </Dropdown.Item>
              <Dropdown.Item
                className="admin"
                title="Remove any previous search results from the cache"
                onClick={()=>{flushCache(authData); setShowDropdown(false)}}
                key="flush"
              >
                Flush Cache
              </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate a warning message"
                  onClick={() => {setShowDropdown(false);toast.warning("This is a test warning");}}
                  key="toast-warning"
                >
                  Generate Toast Warning
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate an error message"
                  onClick={() => {setShowDropdown(false);toast.error("This is a test error");}}
                  key="toast-error"
                >
                  Generate Toast Error
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate a truly uncaught exception via setTimeout (escapes React event system)"
                  onClick={() => {setShowDropdown(false); setTimeout(() => { throw Error("[TEST] Test Sentry uncaught exception") }, 0)}}
                  key="sentry-uncaught-exception"
                >
                  Test Sentry (uncaught exception)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate an unhandled program error within a promise"
                  onClick={() => {
                    setShowDropdown(false);
                    fetch("/").then(() => {
                      throw Error("[TEST] Test Sentry uncaught exception within promise")
                    });
                  }}
                  key="sentry-uncaught-promise-exception"
                >
                  Test Sentry (promise w/unhandled exception)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Throw during React render to test error boundary capture"
                  onClick={() => {setShowDropdown(false); setTriggerErrorBoundary(true)}}
                  key="sentry-error-boundary"
                >
                  Test Sentry (error boundary)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate a console error"
                  onClick={() => {setShowDropdown(false); console.error("[TEST] Test Sentry console error")}}
                  key="sentry-console-error"
                >
                  Test Sentry (console error)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate a sentry message"
                  onClick={() => {setShowDropdown(false); captureMessage("[TEST] Test Sentry message", "error", {text: "test data"})}}
                  key="sentry-message"
                >
                  Test Sentry (sentry message)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate a server uncaught exception"
                  onClick={() => {setShowDropdown(false);reload({uncaught_exception: true}, authData)}}
                  key="sentry-server-uncaught-exception"
                >
                  Test Sentry/server logging (uncaught exception)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Generate a handled server exception"
                  onClick={() => {setShowDropdown(false);reload({exception: true}, authData)}}
                  key="sentry-server-exception"
                >
                  Test server logging (exception)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Log a server error"
                  onClick={() => {setShowDropdown(false);reload({error: true}, authData)}}
                  key="sentry-server-error"
                >
                  Test server logging (error)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Log a server warning"
                  onClick={() => {setShowDropdown(false);reload({warning: true}, authData)}}
                  key="sentry-server-warning"
                >
                  Test server logging (warning)
                </Dropdown.Item>
                <Dropdown.Item
                  className="admin"
                  title="Log server info"
                  onClick={() => {setShowDropdown(false);reload({info: true}, authData)}}
                  key="sentry-server-info"
                >
                  Test server logging (info)
                </Dropdown.Item>
                <Dropdown.Item className="admin-toggle" key="simulate-stream-blocked">
                  <Checkbox
                    label={"Simulate streaming blocked"}
                    title={"Simulate a corporate proxy blocking SSE connections — forces polling fallback"}
                    checked={simulateStreamBlocked}
                    onChange={val => {
                      if (val) {
                        sessionStorage.setItem("simulate_sse_blocked", "1");
                      } else {
                        sessionStorage.removeItem("simulate_sse_blocked");
                      }
                      setSimulateStreamBlocked(val);
                      Analytics.event(Analytics.EVENTS.SETTINGS_CHANGE, { setting: "simulate_streaming_blocked", value: val });
                    }}
                  />
                </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
      <ErrorBoundaryTrigger trigger={triggerErrorBoundary} />
    </span>
  );
};

export default inject("userStore", "appStatusStore")(observer(SettingsMenu));
